import styles from "@/asserts/styles/formSection.module.less";
import { MaterialFileFormFields } from "@/components/commonFormItems/MaterialFileFormFields";
import ModalSectionHeader from "@/components/ui/ModalSectionHeader";
import { purchaseGroupOptions, unitOptions } from "@/constants";
import { CascaderFields } from "@/pages/requirementManagement/purchaseRequestManagement/components/CascaderFields";
import type { RldReferenceProductRequisitionFormValue } from "@/pages/requirementManagement/purchaseRequestManagement/type";
import { Col, DatePicker, Form, Input, InputNumber, Row, Select } from "antd";
import { useRldFormEditable } from "./hooks/useRldFormEditable";
import { useMaterialGroupOptions } from "@/hooks/services/useSelectOptionsQuery";
import { useTranslation } from "react-i18next";

export const ApplicationInfoFormFields = () => {
  const { t } = useTranslation("purchaseRequestManagement");
  const { t: tCommon } = useTranslation("common");

  const { isMainFormDisabled, isSupplierInfoFormDisabled } =
    useRldFormEditable();

  const materialGroupOptions = useMaterialGroupOptions();

  return (
    <div className={styles.formSection}>
      <ModalSectionHeader title={t("申请信息")} />

      <div className={styles.sectionBody}>
        <Row gutter={49}>
          <CascaderFields />
        </Row>

        <Row gutter={49}>
          <Col span={8}>
            <Form.Item<RldReferenceProductRequisitionFormValue>
              name="materialNameCn"
              label={t("RLD中文通用名称")}
              rules={[
                {
                  required: true,
                  message: t("请输入RLD中文通用名称"),
                },
              ]}
            >
              <Input placeholder={t("请输入")} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<RldReferenceProductRequisitionFormValue>
              name="materialNameEn"
              label={t("RLD英文通用名称")}
            >
              <Input placeholder={t("请输入")} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<RldReferenceProductRequisitionFormValue>
              name="productNameCn"
              label={t("RLD中文商品名称")}
            >
              <Input placeholder={t("请输入")} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<RldReferenceProductRequisitionFormValue>
              name="productNameEn"
              label={t("RLD英文商品名称")}
            >
              <Input placeholder={t("请输入")} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item<RldReferenceProductRequisitionFormValue>
              name="isSuggestedSupplier"
              label={t("是否指定建议供应商")}
              rules={[
                {
                  required: true,
                  message: t("请选择是否指定建议供应商"),
                },
              ]}
            >
              <Select
                placeholder={t("请选择")}
                options={[
                  { label: tCommon("是"), value: 1 },
                  { label: tCommon("否"), value: 0 },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<RldReferenceProductRequisitionFormValue>
              name="suggestedSupplierName"
              label={t("指定建议供应商名称")}
            >
              <Input placeholder={t("请输入")} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item<RldReferenceProductRequisitionFormValue>
              name="suggestedReason"
              label={t("指定建议原因")}
            >
              <Input.TextArea rows={2} placeholder={t("请输入")} />
            </Form.Item>
          </Col>
        </Row>
        <MaterialFileFormFields
          fieldName="suggestedSupplierCoa"
          label={t("请附上指定建议的RLD说明资料")}
          required={false}
          hiddenRemove={isMainFormDisabled}
        />

        <Row gutter={49}>
          <Col span={8}>
            <Form.Item<RldReferenceProductRequisitionFormValue>
              name="listingRegion"
              label={t("上市国家或地区")}
            >
              <Input placeholder={t("请输入")} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<RldReferenceProductRequisitionFormValue>
              name="mainIngredients"
              label={t("主要成分")}
            >
              <Input placeholder={t("请输入")} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<RldReferenceProductRequisitionFormValue>
              name="spec"
              label={t("规格")}
            >
              <Input placeholder={t("请输入")} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<RldReferenceProductRequisitionFormValue>
              name="dosageForm"
              label={t("剂型")}
            >
              <Input placeholder={t("请输入")} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<RldReferenceProductRequisitionFormValue>
              name="packagingSpec"
              label={t("包装规格")}
              rules={[
                {
                  required: true,
                  message: t("请输入包装规格"),
                },
              ]}
            >
              <Input placeholder={t("请输入")} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Row gutter={8}>
              <Col span={16}>
                <Form.Item<RldReferenceProductRequisitionFormValue>
                  name="applicationQty"
                  label={t("需求数量")}
                >
                  <InputNumber
                    min={1}
                    placeholder={t("请输入")}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item<RldReferenceProductRequisitionFormValue>
                  name="applicationUnit"
                  label={t("单位")}
                >
                  <Select placeholder={t("请选择")} options={unitOptions} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={8}>
            <Form.Item<RldReferenceProductRequisitionFormValue>
              name="projectNo"
              label={t("关联项目编号")}
            >
              <Input placeholder={t("请输入")} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<RldReferenceProductRequisitionFormValue>
              name="deliveryDate"
              label={t("要求到货时间")}
            >
              <DatePicker placeholder={t("请选择")} style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item<RldReferenceProductRequisitionFormValue>
              name="materialGroup"
              label={t("物料组")}
              rules={[
                {
                  required: true,
                  message: t("请选择物料组"),
                },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder={t("请选择")}
                options={materialGroupOptions}
                disabled={isSupplierInfoFormDisabled}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item<RldReferenceProductRequisitionFormValue>
              name="purchaseGroup"
              label={t("采购组")}
              rules={[
                {
                  required: true,
                  message: t("请选择采购组"),
                },
              ]}
            >
              <Select
                placeholder={t("请选择")}
                options={purchaseGroupOptions}
                disabled={isSupplierInfoFormDisabled}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>
    </div>
  );
};
